import { Link } from 'react-router-dom';
import fullLogoSrc from '@/assets/branding/source/pointetracer-full-logo.png';

export default function LandingHero() {
  return (
    <section className="relative overflow-hidden pt-24 pb-20 md:pt-32 md:pb-28">
      {/* Radial brand glow — upper-right quadrant */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-30"
        style={{
          background:
            'radial-gradient(circle at top right, var(--color-brand-primary) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16">
          {/* Left — copy + CTAs */}
          <div className="flex-1 text-center lg:text-left">
            {/* Eyebrow label */}
            <p
              className="mb-5 inline-block text-xs font-medium uppercase tracking-widest"
              style={{ color: 'var(--color-brand-primary)', letterSpacing: '0.12em' }}
            >
              Movement analysis, measured.
            </p>

            {/* Headline */}
            <h1
              className="text-display-xl mb-6"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Score every movement.
              <br />
              <span style={{ color: 'var(--color-brand-primary)' }}>See every detail.</span>
            </h1>

            {/* Subhead */}
            <p
              className="text-body-lg mb-10 max-w-xl mx-auto lg:mx-0"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              PointeTracer compares an athlete&apos;s movement against a coach&apos;s reference
              template and produces an instrument-grade evaluation in seconds — phase-by-phase
              scores, body-region heatmaps, and AI-generated coaching narrative.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg px-7 py-3.5 text-base font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  backgroundColor: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                    'var(--color-brand-primary-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                    'var(--color-brand-primary)';
                }}
              >
                Have an invitation? Sign in
              </Link>

              <a
                href="mailto:hello@pointetracer.com?subject=PointeTracer%20invitation%20request"
                className="inline-flex items-center justify-center rounded-lg px-7 py-3.5 text-base font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  border: '1.5px solid var(--color-border-strong)',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor =
                    'var(--color-surface-raised-2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                }}
              >
                Want to try PointeTracer? Email us
              </a>
            </div>
          </div>

          {/* Right — logo visual */}
          <div className="flex-shrink-0 flex items-center justify-center">
            <div
              className="relative flex items-center justify-center rounded-3xl p-8"
              style={{
                background: 'var(--color-surface-raised-1)',
                border: '1px solid var(--color-border-subtle)',
                boxShadow:
                  '0 0 60px rgba(59, 130, 246, 0.18), 0 0 120px rgba(59, 130, 246, 0.08)',
              }}
            >
              {/* Glow ring */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-3xl"
                style={{
                  background:
                    'radial-gradient(circle at center, rgba(59,130,246,0.12) 0%, transparent 70%)',
                }}
              />
              <img
                src={fullLogoSrc}
                alt="PointeTracer logo"
                className="relative w-40 h-40 md:w-56 md:h-56 object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
